<template>
    <connection-settings-popup
        :is-new="isNew"
        :config="conditionsModel"
        :validations="$options.validations.conditionsModel"
        :comment-id="commentId"
        :resources="{
            deletePromptTitle: resource('JourneyBuilder_DedupBase_Delete_Title'),
            deletePromptBody: resource('JourneyBuilder_DedupBase_Delete_Body')
        }"

        @save="save()"
        @delete="deleteCheckpoint()"
        @display-errors="displayErrors()"
        @hidden="$emit('hidden')"
    >
        <template #title>
            <div class="w-100">
                <h3 class="d-inline-block">
                    {{resource('JourneyBuilder_DeduplicationBaseSettings_Title')}}
                </h3>

                <help-popover
                    :title="resource('JourneyBuilder_DeduplicationBaseSettings_Title')"
                    :content="resource('JourneyBuilder_DeduplicationBaseSettings_Description')"
                />

                <template v-if="displayJourneyFilters">
                    <div class="mt-3">
                        <h4 class="d-inline-block mr-1">
                            {{resource('JourneyComposer_GenericFiltersModal_Title')}}
                        </h4>

                        <help-popover :content="resource('JourneyComposer_FilterModal_GenericFiltersExplanation')" />
                    </div>

                    <div class="row form-group">
                        <div class="col-12 text-center">
                            <button class="btn btn-outline-primary" @click="openFilterSettings()"><i class="fas fa-pencil-alt mr-1" /> {{resource('JourneyBuilder_FilterExclusionSettings_Button_EditFilters')}}</button>
                        </div>
                    </div>

                    <template v-for="filterGroup in filters">
                        <div class="row form-group no-gutters filter-group" :key="filterGroup.id">
                            <div class="offset-1 col-11">
                                <brick-filter-condition v-for="filter in filterGroup.children" :field="filter.field" :value="filter.value" />
                            </div>
                        </div>
                    </template>
                </template>
            </div>
        </template>

        <template #body>
            <settings-property :label="resource('JourneyBuilder_DeduplicationBaseSettings_Product_DisplayName')">
                <template #input>
                    <v-select
                        class="form-control"

                        v-model="productModel"
                        :options="productCheckOptions"
                        :disabled="!canWrite || !productCheckOptions.length"
                        :clearable="false"

                        searchable
                        filterable
                    />
                </template>
            </settings-property>

            <hr />

            <h4>{{resource("JourneyBuilder_JourneyBrick_SettingsHeader")}}</h4>
            <brick-filter-settings-editor
                ref="editor"

                :conditions="conditionsModel"
                :includedCategoryFilters="[ 'Audience', 'NotAudience' ]"
                idField="id"
                @conditions-changed="conditionsModel = $event"
            />
            <error-message v-if="!$v.conditionsModel.required" :message="resource('JourneyComposer_GroupRequired_ErrorMessage')" />
        </template>
    </connection-settings-popup>
</template>

<script>
import api from '@modules/JourneyBuilder/api';
import resources from '@core/Util/Mixins/resources';
import HelpPopover from '@modules/JourneyBuilder/Components/Elements/HelpPopover.vue';
import BrickFilterSettingsEditor from "./BrickFilterSettingsEditor";
import BrickFilterCondition from "./BrickFilterCondition";
import { NewComment } from '@modules/JourneyBuilder/Components/Composer/CanvasArea/Bricks/Comments';
import ErrorMessage from "@core/Components/Errors/ErrorMessage";
import { ToggleButton } from 'vue-js-toggle-button';
import conditionExpansion from './condition-expansion';
import {validationMixin} from 'vuelidate';
import { required } from 'vuelidate/lib/validators';
import {conditionGroupValidations} from "./validations";
import { deduplicationBaseCheckpoint, filterExclusionCheckpoint } from '@modules/JourneyBuilder/Bricks/checkpoint-definitions';
import permissionsMixin from '@modules/JourneyBuilder/Mixins/journey-permissions';
import {mapMutations} from "vuex";
import injectJourneyMixin from '@modules/JourneyBuilder/Mixins/inject-journey';
import ConnectionSettingsPopup from '../ConnectionSettingsPopup';
import VSelect from 'vue-select';
import {bootstrapDangerGrowl} from '@core/Util/growler';
import SettingsProperty from '../SettingsProperty'

export default {
    components: {
        BrickFilterSettingsEditor,
        BrickFilterCondition,
        HelpPopover,
        NewComment,
        ToggleButton,
        ErrorMessage,
        ConnectionSettingsPopup,
        VSelect,
        SettingsProperty
    },
    
    mixins: [ resources, permissionsMixin, injectJourneyMixin, validationMixin ],

    props: {
        config: Object,
        connection: Object,
        isNew: Boolean
    },
    
    data: () => ({
        productCheckOptions: [],
        
        configuration: { 
            conditions: [],
            product: undefined
        }
    }),

    computed: { 
        commentId() {
            return "connection:" + this.connection.id + ":" + deduplicationBaseCheckpoint.name;
        },

        filters() {
            return conditionExpansion.inflateGenericFilters(this.journey.config.filters);
        },
        
        conditionsModel: {
            get(){
                return this.configuration.conditions;
            },
            set(value){
                this.configuration.conditions = value;
            }
        },
        
        productModel: {
            get(){
                return this.productCheckOptions.find(opt => opt.id === this.configuration.product);
            },
            set(value){
                this.configuration.product = value.id;
            }
        },

        displayJourneyFilters(){
            return !filterExclusionCheckpoint.isActiveForConnection(this.connection);
        }
    },

    validations: {
        conditionsModel: {
            required,
            ...conditionGroupValidations 
        }
    },

    mounted() {
        if (this.config.dedupBase) {
            /// Map [{ field: a, value: [ 1, 2 ] }, { field: b, value: [ 3 ] }], [{ field: c, value: [ 4 ]}]
            /// to
            /// [[{ field: a, value: { field: a, id: .., value: 1 } }, { field: a, value: { field: a, id: .., value: 2 } } }], [{ field: b, value: { field: b, id: .., value: 3 }] [{ field: c, value: { field: c, id: .., value: 4 } }]]

            this.configuration.product = this.config.dedupBase.product;
            
            this.loadProductCheckOptions().then(() => {
                //Fix legacy configuration/init new configuration
                if(typeof this.configuration.product !== 'string') {
                    if(this.productCheckOptions.length) {
                        this.productModel = this.productCheckOptions[0];
                    }
                } 
            });
            
            const groupedConditions = (this.config.dedupBase.conditions || [])
                .map(
                    group => group.flatMap(
                        condition => condition.value.map(audienceId => ({ field: condition.field, value: audienceId }))
                    )
                );

            this.configuration.conditions = conditionExpansion.inflate(groupedConditions);
        }
    },

    methods: {
        ...mapMutations('journeyBuilder/composer', ['toggleJourneyFiltersEditor']),
        
        addField() {
            this.dedupFields.push("");
        },

        removeField(index) {
            this.dedupFields.splice(index, 1);
        },

        save() {
            /// Map [[{ field: a, value: 1 }, { field: a, value: 2 }, { field: b, value: 3 }], [{ field: c, value: 4 }]]
            /// to
            /// [{ a: [1, 2], b: [3] }], [{ c: [4] }]
            /// and then to
            /// [{ field: a, value: [1, 2] }, { field: b, value: [3] }],[{ field: c, value [4] }]
            const mapped = conditionExpansion.deflate(this.configuration.conditions).map(x => {
                const map = x.reduce((acc, curr) => {
                    if (!acc[curr.field]) {
                        acc[curr.field] = [];
                    }

                    acc[curr.field].push(curr.value);
                    return acc;
                }, {});

                const res = [];
                for(let i in map) {
                    res.push({
                        "field": i,
                        "value": map[i]
                    });
                }
                return res;
            });

            this.$emit("save", {
                dedupBase: { 
                    product: this.configuration.product,
                    conditions: mapped
                }
            });
        },

        deleteCheckpoint() {
            this.$emit('delete-connection-checkpoint', { connection: this.connection, checkpoint: 'dedupBase' });
        },

        openFilterSettings(){
            this.toggleJourneyFiltersEditor(true);
        },

        displayErrors() {
            this.$refs.editor.displayErrors();
        },
        
        async loadProductCheckOptions() {
            try {
                this.productCheckOptions = await api.getProductCheckOptions();
            } catch (e) {
                bootstrapDangerGrowl(e);
            }
        }
    },
}
</script>

<style scoped>
.filter-group {
    background-color: #E3E3E3;
}
</style>